import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Field";
import { TwoFactorStep } from "@/components/TwoFactorStep";
import { api } from "@/lib/api";
import { errText } from "@/lib/aadhaar";

const ROLE_NAME = { teacher: "trainer", principal: "principal", admin: "admin", student: "student" };

export default function AdminLogin({ role = "admin" }) {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);

  const finish = (data) => {
    localStorage.setItem(`rs_${role}_token`, data.token);
    nav(`/${role}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", f);
      const got = data.mfa_required ? data.role : data.user.role;
      if (got !== role) {
        setErr(`This is not a ${ROLE_NAME[role]} account. Please use the correct sign-in page.`);
        return;
      }
      if (data.mfa_required) setMfa(data); else finish(data);
    } catch (e2) {
      setErr(errText(e2.response?.data?.detail, "Login failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[75vh] grid place-items-center bg-[#E8F0FE] px-5" data-testid="admin-login-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 w-full max-w-sm">
        {mfa ? <TwoFactorStep mfa={mfa} onSuccess={finish} /> : (
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div className="w-11 h-11 rounded-xl bg-[#F0F4F8] grid place-items-center"><Lock className="w-5 h-5 text-[#007A8A]" /></div>
            <h1 className="text-2xl font-extrabold text-[#0A192F]" data-testid="login-title">{{ teacher: "Trainer sign in", principal: "Principal sign in", admin: "Admin sign in" }[role]}</h1>
            {role === "principal" && <p className="text-xs text-slate-500" data-testid="principal-2fa-note">School accounts are protected with two-step verification (email code or authenticator app).</p>}
            <Field id="admin_email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <Field id="admin_password" label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
            {err && <p className="text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
            <Button type="submit" disabled={loading} className="w-full rs-cta h-11 rounded-full" data-testid="admin-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
